"use client"

import { Button, IconButton } from "@chakra-ui/react"
import * as React from "react"
import {
  HiChevronLeft,
  HiChevronRight,
  HiMiniEllipsisHorizontal,
} from "react-icons/hi2"
import { LinkButton } from "./link-button"

interface PaginationContextValue {
  page: number
  totalPages: number
  setPage: (page: number) => void
  getHref?: (page: number) => string
}

const PaginationContext = React.createContext<PaginationContextValue>({
  page: 1,
  totalPages: 1,
  setPage: () => {},
})

function computePages(
  current: number,
  total: number,
): Array<{ type: "page"; value: number } | { type: "ellipsis"; key: string }> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => ({
      type: "page" as const,
      value: i + 1,
    }))
  }
  const items: Array<
    { type: "page"; value: number } | { type: "ellipsis"; key: string }
  > = []
  const win = 1
  const start = Math.max(2, current - win)
  const end = Math.min(total - 1, current + win)

  items.push({ type: "page", value: 1 })
  if (start > 2) items.push({ type: "ellipsis", key: "ellipsis-start" })
  for (let i = start; i <= end; i++) items.push({ type: "page", value: i })
  if (end < total - 1) items.push({ type: "ellipsis", key: "ellipsis-end" })
  items.push({ type: "page", value: total })
  return items
}

export interface PaginationRootProps {
  page: number
  count: number
  pageSize: number
  onPageChange: (details: { page: number }) => void
  getHref?: (page: number) => string
  children?: React.ReactNode
  [key: string]: unknown
}

export const PaginationRoot = ({
  page,
  count,
  pageSize,
  onPageChange,
  getHref,
  children,
}: PaginationRootProps) => {
  const totalPages = Math.max(1, Math.ceil(count / pageSize))
  const setPage = (p: number) => {
    const clamped = Math.max(1, Math.min(totalPages, p))
    if (clamped !== page) onPageChange({ page: clamped })
  }
  return (
    <PaginationContext.Provider value={{ page, totalPages, setPage, getHref }}>
      {children}
    </PaginationContext.Provider>
  )
}

export const PaginationPrevTrigger = () => {
  const { page, setPage, getHref, totalPages } =
    React.useContext(PaginationContext)
  const isDisabled = page <= 1

  if (getHref) {
    return (
      <LinkButton
        href={page > 1 ? getHref(page - 1) : undefined}
        variant="ghost"
        size="sm"
      >
        <HiChevronLeft />
      </LinkButton>
    )
  }

  void totalPages

  return (
    <IconButton
      variant="ghost"
      size="sm"
      disabled={isDisabled}
      onClick={() => setPage(page - 1)}
      aria-label="Previous page"
    >
      <HiChevronLeft />
    </IconButton>
  )
}

export const PaginationNextTrigger = () => {
  const { page, setPage, getHref, totalPages } =
    React.useContext(PaginationContext)
  const isDisabled = page >= totalPages

  if (getHref) {
    return (
      <LinkButton
        href={page < totalPages ? getHref(page + 1) : undefined}
        variant="ghost"
        size="sm"
      >
        <HiChevronRight />
      </LinkButton>
    )
  }

  return (
    <IconButton
      variant="ghost"
      size="sm"
      disabled={isDisabled}
      onClick={() => setPage(page + 1)}
      aria-label="Next page"
    >
      <HiChevronRight />
    </IconButton>
  )
}

const PaginationEllipsis = () => (
  <Button variant="plain" size="sm" aria-hidden disabled>
    <HiMiniEllipsisHorizontal />
  </Button>
)

const PaginationItem = ({ value }: { value: number }) => {
  const { page, setPage, getHref } = React.useContext(PaginationContext)
  const isCurrent = page === value

  if (getHref) {
    return (
      <LinkButton
        href={getHref(value)}
        variant={isCurrent ? "outline" : "ghost"}
        size="sm"
      >
        {value}
      </LinkButton>
    )
  }

  return (
    <Button
      variant={isCurrent ? "outline" : "ghost"}
      size="sm"
      aria-current={isCurrent ? "page" : undefined}
      onClick={() => setPage(value)}
    >
      {value}
    </Button>
  )
}

export const PaginationItems = () => {
  const { page, totalPages } = React.useContext(PaginationContext)
  const pages = computePages(page, totalPages)
  return (
    <>
      {pages.map((p) =>
        p.type === "ellipsis" ? (
          <PaginationEllipsis key={p.key} />
        ) : (
          <PaginationItem key={p.value} value={p.value} />
        ),
      )}
    </>
  )
}

export const PaginationPageText = () => {
  const { page, totalPages } = React.useContext(PaginationContext)
  return (
    <span>
      {page} of {totalPages}
    </span>
  )
}
